import React, { useState } from 'react';
import { Gem, ChevronRight } from 'lucide-react';

const eras = [
  {
    id: 'shamanic',
    name: 'Prehistoric Shamanic Pulse',
    years: '3000 – 1500 BCE',
    start: -3000, end: -1500,
    crystals: ['Flint', 'Jasper'],
    role: 'Shamanic Tools',
    color: '#DAA520',
    desc: 'The first awakening. Early humans used flint and jasper as shamanic instruments — tools that cut through both the physical and spiritual planes. These stones held memory before writing existed.',
  },
  {
    id: 'egyptian',
    name: 'Egyptian-Babylonian Enlightenment',
    years: '2500 – 500 BCE',
    start: -2500, end: -500,
    crystals: ['Lapis Lazuli', 'Carnelian'],
    role: 'Priest-Kings',
    color: '#FF7043',
    desc: 'The age of divine rulership. Lapis Lazuli adorned the tombs of pharaohs while Carnelian fueled the courage of Babylonian leaders. Crystals became instruments of spiritual authority.',
  },
  {
    id: 'classical',
    name: 'Classical Greek-Roman Cycle',
    years: '800 BCE – 400 CE',
    start: -800, end: 400,
    crystals: ['Quartz', 'Hematite'],
    role: 'Oracle Temples',
    color: '#E91E63',
    desc: 'Philosophy met the mineral kingdom. Quartz amplified the visions at Delphi while Hematite grounded warriors and thinkers alike. Crystals served as bridges between reason and revelation.',
  },
  {
    id: 'medieval',
    name: 'Mystical Middle Ages',
    years: '400 – 1400 CE',
    start: 400, end: 1400,
    crystals: ['Amethyst', 'Onyx'],
    role: 'Mystic Orders',
    color: '#E040FB',
    desc: 'Hidden knowledge preserved in stone. Amethyst guarded the sobriety of mystic orders while Onyx shielded sacred rituals from profane energies. The pulse went underground — but never stopped.',
  },
  {
    id: 'renaissance',
    name: 'Renaissance & Reformation',
    years: '1400 – 1700 CE',
    start: 1400, end: 1700,
    crystals: ['Clear Quartz', 'Sapphire'],
    role: 'Scholars & Alchemists',
    color: '#42A5F5',
    desc: 'The pulse resurfaces through art and alchemy. Clear Quartz powered the clarity of Da Vinci\'s vision. Sapphire channeled the divine wisdom sought by reformation thinkers.',
  },
  {
    id: 'industrial',
    name: 'Industrial & Scientific Age',
    years: '1700 – 2000 CE',
    start: 1700, end: 2000,
    crystals: ['Pyrite', 'Shungite'],
    role: 'Engineers & Inventors',
    color: '#00BCD4',
    desc: 'Crystals entered the machine age. Pyrite sparked innovation in the minds of inventors. Shungite emerged as a natural shield against the electromagnetic frequencies of a wired world.',
  },
  {
    id: 'digital',
    name: 'Digital & Consciousness Shift',
    years: '2000 CE – Present',
    start: 2000, end: 2600,
    crystals: ['Moldavite', 'Selenite', 'Black Tourmaline'],
    role: 'Energy & Digital Shift',
    color: '#00C853',
    desc: 'The current pulse. Moldavite accelerates transformation, Selenite purifies the noise, and Black Tourmaline shields against digital and psychic interference. We are living inside the shift.',
    current: true,
  },
];

const PulseTimeline = () => {
  const [activeEra, setActiveEra] = useState('digital');
  const active = eras.find((e) => e.id === activeEra);

  return (
    <div className="w-full" data-testid="pulse-timeline">
      {/* Era Selector Row */}
      <div className="flex gap-2 overflow-x-auto pb-4 scrollbar-hide mb-6">
        {eras.map((era) => (
          <button
            key={era.id}
            onClick={() => setActiveEra(era.id)}
            className={`flex-shrink-0 px-4 py-3 rounded-xl text-left transition-all duration-300 border ${
              activeEra === era.id
                ? 'bg-[#1e1919] border-transparent shadow-xl scale-[1.02]'
                : 'bg-white/80 border-[#e8e4de] hover:border-[#9D4EDD]/40 hover:shadow-md'
            }`}
            style={activeEra === era.id ? { boxShadow: `0 8px 30px ${era.color}30` } : {}}
            data-testid={`timeline-era-${era.id}`}
          >
            <div className="flex items-center gap-2 mb-1">
              <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: era.color, opacity: activeEra === era.id ? 1 : 0.5 }} />
              <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${activeEra === era.id ? 'text-white' : 'text-[#7A7A9A]'}`}>
                {era.years}
              </span>
              {era.current && (
                <span className="px-1.5 py-0.5 text-[8px] font-bold uppercase bg-[#00C853]/20 text-[#00C853] rounded-full">Now</span>
              )}
            </div>
            <p className={`text-sm font-semibold whitespace-nowrap ${activeEra === era.id ? 'text-white' : 'text-[#1e1919]'}`}>
              {era.name.length > 28 ? era.name.slice(0, 28) + '...' : era.name}
            </p>
          </button>
        ))}
      </div>

      {/* Visual Timeline Bar */}
      <div className="relative h-2 bg-[#e8e4de] rounded-full mb-8 overflow-hidden">
        {eras.map((era) => {
          const totalSpan = 5600; // -3000 to 2600
          const left = ((era.start + 3000) / totalSpan) * 100;
          const width = ((era.end - era.start) / totalSpan) * 100;
          return (
            <div
              key={era.id}
              className={`absolute top-0 h-full rounded-full transition-all duration-500 cursor-pointer ${activeEra === era.id ? 'z-10 scale-y-150' : 'opacity-50 hover:opacity-80'}`}
              style={{ left: `${left}%`, width: `${width}%`, backgroundColor: era.color }}
              onClick={() => setActiveEra(era.id)}
            />
          );
        })}
      </div>

      {/* Active Era Detail */}
      {active && (
        <div className="bg-white rounded-2xl border border-[#e8e4de] shadow-lg overflow-hidden transition-all duration-300" data-testid="timeline-detail">
          <div className="p-1.5">
            <div className="rounded-xl p-6" style={{ background: `linear-gradient(135deg, ${active.color}15, ${active.color}05)` }}>
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: active.color }} />
                    <span className="text-xs font-bold uppercase tracking-wider text-[#7A7A9A]">{active.years}</span>
                    {active.current && <span className="px-2 py-0.5 text-xs font-bold bg-[#00C853]/20 text-[#00C853] rounded-full animate-pulse">Current Pulse</span>}
                  </div>
                  <h3 className="font-serif text-2xl text-[#1e1919] mb-3">{active.name}</h3>
                  <p className="text-[#4A4A6A] leading-relaxed">{active.desc}</p>
                </div>
                <div className="md:w-64 flex-shrink-0 bg-white rounded-xl p-5 border border-[#e8e4de]">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#7A7A9A] mb-3">Crystal Resonance</p>
                  <div className="space-y-2">
                    {active.crystals.map((crystal, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${active.color}20` }}>
                          <Gem className="w-4 h-4" style={{ color: active.color }} />
                        </div>
                        <span className="text-sm font-semibold text-[#1e1919]">{crystal}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-[#7A7A9A] mt-3 italic flex items-center gap-1">
                    <ChevronRight className="w-3 h-3" /> {active.role}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PulseTimeline;
